/**
 * Custom implementation of alpha function that doesn't rely on MUI's implementation
 * This avoids the 'Cannot read properties of undefined (reading 'type')' error
 * 
 * @param {string|undefined} color - The color to apply transparency to
 * @param {number} value - The alpha value (0-1)
 * @param {string} fallback - Fallback color if the input is undefined
 * @returns {string} - The color with alpha applied or fallback
 */
export const safeAlpha = (color, value = 0.1, fallback = 'rgba(0, 0, 0, 0.1)') => {
  if (!color) return fallback;
  
  try {
    // Handle different color formats
    if (color.startsWith('#')) {
      // Convert hex to rgba
      let r, g, b;
      if (color.length === 4) {
        // #RGB format
        r = parseInt(color[1] + color[1], 16);
        g = parseInt(color[2] + color[2], 16);
        b = parseInt(color[3] + color[3], 16);
      } else {
        // #RRGGBB format
        r = parseInt(color.slice(1, 3), 16);
        g = parseInt(color.slice(3, 5), 16);
        b = parseInt(color.slice(5, 7), 16);
      }
      return `rgba(${r}, ${g}, ${b}, ${value})`;
    } else if (color.startsWith('rgb(')) {
      // Convert rgb to rgba
      const rgb = color.match(/\d+/g);
      if (rgb && rgb.length >= 3) {
        return `rgba(${rgb[0]}, ${rgb[1]}, ${rgb[2]}, ${value})`;
      }
    } else if (color.startsWith('rgba(')) {
      // Already rgba, just modify the alpha
      const rgba = color.match(/\d+(\.\d+)?/g);
      if (rgba && rgba.length >= 4) {
        return `rgba(${rgba[0]}, ${rgba[1]}, ${rgba[2]}, ${value})`;
      }
    }
    
    // For any other format or if parsing fails, return the fallback
    return fallback;
  } catch (error) {
    console.warn('Error applying alpha to color:', error);
    return fallback;
  }
};

/**
 * Get a safe color value from theme with fallback
 * 
 * @param {object} theme - MUI theme object
 * @param {string} path - Dot notation path to color (e.g., 'palette.primary.main')
 * @param {string} fallback - Fallback color
 * @returns {string} - The color or fallback
 */
export const safeColor = (theme, path, fallback = '#757575') => {
  if (!theme) return fallback;
  
  try {
    const parts = path.split('.');
    let result = theme;
    
    for (const part of parts) {
      if (result && result[part] !== undefined) {
        result = result[part];
      } else {
        return fallback;
      }
    }
    
    return typeof result === 'string' ? result : fallback;
  } catch (error) {
    console.warn('Error getting color from theme:', error);
    return fallback;
  }
};

/**
 * MUI theme color constants for use as fallbacks
 */
export const defaultColors = {
  primary: {
    main: '#1976d2',
    light: '#42a5f5',
    dark: '#1565c0'
  },
  secondary: {
    main: '#9c27b0',
    light: '#ba68c8',
    dark: '#7b1fa2'
  },
  error: {
    main: '#d32f2f',
    light: '#ef5350',
    dark: '#c62828'
  },
  warning: {
    main: '#ed6c02',
    light: '#ff9800',
    dark: '#e65100'
  },
  info: {
    main: '#0288d1',
    light: '#03a9f4',
    dark: '#01579b'
  },
  success: {
    main: '#2e7d32',
    light: '#4caf50',
    dark: '#1b5e20'
  },
  grey: {
    50: '#fafafa',
    100: '#f5f5f5',
    200: '#eeeeee',
    300: '#e0e0e0',
    400: '#bdbdbd',
    500: '#9e9e9e',
    600: '#757575',
    700: '#616161',
    800: '#424242',
    900: '#212121'
  }
};
